(() => {
  if (!customElements.get('titiroba-hero')) {

  class TitirobaHero extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        this.documentHidden = document.hidden;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.reducedMotion?.addEventListener?.('change', this.onMotionChange);
        this.observer?.observe(this);
        this.updateToggle();
        this.syncAutoplay();
        return;
      }

      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.videos = this.slides.map((slide) => slide.querySelector('[data-hero-video]'));
      this.toggleButton = this.querySelector('[data-hero-toggle]');
      this.pauseIcon = this.querySelector('[data-hero-pause-icon]');
      this.playIcon = this.querySelector('[data-hero-play-icon]');
      this.status = this.querySelector('[data-hero-status]');
      this.currentIndex = 0;
      this.interval = Number(this.dataset.autoplay) || 7000;
      this.userPaused = false;
      this.interactionOverride = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.documentHidden = document.hidden;
      this.inView = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.slides.length === 0) return;

      this.dataset.initialized = 'true';
      this.classList.add('is-enhanced');
      this.slides.forEach((slide) => { slide.hidden = false; });
      this.showSlide(0, false);

      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showSlide(this.currentIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showSlide(this.currentIndex + 1);
        }
      };
      this.onMouseEnter = () => { this.hoverPaused = true; this.syncAutoplay(); };
      this.onMouseLeave = () => {
        this.hoverPaused = false;
        if (!this.focusPaused) this.interactionOverride = false;
        this.syncAutoplay();
      };
      this.onFocusIn = () => { this.focusPaused = true; this.syncAutoplay(); };
      this.onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        if (!this.hoverPaused) this.interactionOverride = false;
        this.syncAutoplay();
      };
      this.onVisibilityChange = () => {
        this.documentHidden = document.hidden;
        this.syncAutoplay();
      };
      this.onMotionChange = () => this.syncAutoplay();

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion.addEventListener?.('change', this.onMotionChange);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.heroDot)));
      });

      this.toggleButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.interactionOverride = !this.userPaused;
        this.updateToggle();
        this.syncAutoplay();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio > 0.15;
          this.syncAutoplay();
        }, { threshold: [0, 0.15] });
        this.observer.observe(this);
      }

      this.updateToggle();
      this.syncAutoplay();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.videos?.forEach((video) => video?.pause());
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion?.removeEventListener?.('change', this.onMotionChange);
    }

    showSlide(requestedIndex, restart = true) {
      const total = this.slides.length;
      this.currentIndex = (requestedIndex + total) % total;

      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });

      this.dots.forEach((dot, index) => {
        const active = index === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });

      if (this.status) this.status.textContent = `スライド ${this.currentIndex + 1} / ${total}`;
      if (restart) this.syncAutoplay();
      else this.syncVideoPlayback();
    }

    updateToggle() {
      if (!this.toggleButton) return;
      this.toggleButton.setAttribute('aria-label', this.userPaused ? 'スライドショーを再生' : 'スライドショーを一時停止');
      if (this.pauseIcon) this.pauseIcon.hidden = this.userPaused;
      if (this.playIcon) this.playIcon.hidden = !this.userPaused;
    }

    syncAutoplay() {
      window.clearInterval(this.timer);
      this.timer = undefined;

      const paused = this.isPlaybackPaused();

      if (!paused) {
        this.timer = window.setInterval(() => this.showSlide(this.currentIndex + 1, false), this.interval);
      }

      this.syncVideoPlayback();
    }

    isPlaybackPaused() {
      return this.slides.length < 2
        || this.userPaused
        || (!this.interactionOverride && (this.hoverPaused || this.focusPaused))
        || this.documentHidden
        || !this.inView
        || this.reducedMotion.matches;
    }

    syncVideoPlayback() {
      const playbackPaused = this.isPlaybackPaused();

      this.videos.forEach((video, index) => {
        if (!video) return;
        const frame = video.closest('[data-hero-video-frame]');
        const active = index === this.currentIndex;
        const shouldPlay = active && !playbackPaused;

        if (!shouldPlay) {
          video.pause();
          if (!active || this.reducedMotion.matches || this.documentHidden || !this.inView) {
            video.currentTime = 0;
            frame?.classList.remove('is-video-playing');
          }
          return;
        }

        if (video.ended) return;
        const playRequest = video.play();
        if (playRequest?.then) {
          playRequest
            .then(() => {
              if (index === this.currentIndex && !this.isPlaybackPaused()) frame?.classList.add('is-video-playing');
            })
            .catch(() => frame?.classList.remove('is-video-playing'));
        }
      });
    }
  }

    customElements.define('titiroba-hero', TitirobaHero);
  }

  if (!customElements.get('titiroba-product-tabs')) {
    class TitirobaProductTabs extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') {
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);
          document.addEventListener('shopify:block:select', this.onBlockSelect);

          const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
          this.activate(selectedIndex >= 0 ? selectedIndex : 0);
          return;
        }

        this.tabs = Array.from(this.querySelectorAll('[data-product-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-product-panel]'));
        this.tablist = this.querySelector('[data-product-tablist]');

        if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

        this.dataset.initialized = 'true';
        this.classList.add('is-enhanced');
        this.tablist.hidden = false;

        this.onClick = (event) => {
          const tab = event.target.closest('[data-product-tab]');
          if (!tab || !this.contains(tab)) return;
          event.stopPropagation();
          this.activate(this.tabs.indexOf(tab));
        };

        this.onKeydown = (event) => {
          const currentIndex = this.tabs.indexOf(event.target);
          if (currentIndex < 0) return;

          let nextIndex;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.tabs.length - 1;
          if (nextIndex === undefined) return;

          event.preventDefault();
          event.stopPropagation();
          this.activate(nextIndex, true);
        };

        this.onBlockSelect = (event) => {
          const blockId = event.detail?.blockId || event.target?.closest?.('[data-block-id]')?.dataset.blockId;
          if (!blockId) return;
          const selectedIndex = this.tabs.findIndex((tab) => tab.dataset.blockId === blockId);
          if (selectedIndex >= 0) this.activate(selectedIndex);
        };

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.activate(0);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      activate(requestedIndex, moveFocus = false) {
        if (requestedIndex < 0 || requestedIndex >= this.tabs.length) return;
        this.activeIndex = requestedIndex;

        this.tabs.forEach((tab, index) => {
          const active = index === requestedIndex;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && moveFocus) {
            tab.focus({ preventScroll: true });
            tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
          }
        });

        this.panels.forEach((panel, index) => {
          const active = index === requestedIndex;
          panel.hidden = !active;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.tabs[index].id);
          if ('inert' in panel) panel.inert = !active;
          if (active) {
            const grid = panel.querySelector('[data-product-grid]');
            if (grid) grid.scrollLeft = 0;
          }
        });

        window.requestAnimationFrame(() => {
          if (!this.isConnected || this.activeIndex !== requestedIndex) return;
          this.tabs.forEach((tab, index) => {
            tab.setAttribute('aria-selected', String(index === requestedIndex));
          });
        });
      }
    }

    customElements.define('titiroba-product-tabs', TitirobaProductTabs);
  }

  if (!customElements.get('titiroba-kitchen-media')) {
    class TitirobaKitchenMedia extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.video = this.querySelector('[data-kitchen-video]');
        this.toggle = this.querySelector('[data-kitchen-toggle]');
        this.soundToggle = this.querySelector('[data-kitchen-sound-toggle]');
        this.soundLabel = this.querySelector('[data-kitchen-sound-label]');
        this.pauseIcon = this.querySelector('[data-kitchen-pause-icon]');
        this.playIcon = this.querySelector('[data-kitchen-play-icon]');
        if (!this.video || !this.toggle) return;

        this.dataset.initialized = 'true';
        this.classList.add('is-enhanced');
        this.inView = false;
        this.userPaused = false;
        this.manualReducedMotionPlay = false;
        this.documentHidden = document.hidden;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onVisibilityChange = () => {
          this.documentHidden = document.hidden;
          this.syncPlayback();
        };
        this.onMotionChange = () => {
          this.manualReducedMotionPlay = false;
          this.syncPlayback();
        };
        this.onToggle = () => {
          if (!this.video.paused) {
            this.userPaused = true;
            this.manualReducedMotionPlay = false;
            this.video.pause();
            this.classList.remove('is-video-playing');
            this.updateToggle();
            return;
          }

          this.userPaused = false;
          this.manualReducedMotionPlay = this.reducedMotion.matches;
          this.playVideo();
        };
        this.onEnded = () => {
          if (this.reducedMotion.matches) {
            this.manualReducedMotionPlay = false;
            this.video.currentTime = 0;
            this.classList.remove('is-video-playing');
            this.updateToggle();
          }
        };

        this.onSoundToggle = () => {
          this.video.muted = !this.video.muted;
          this.updateSoundToggle();
        };
        this.onVolumeChange = () => this.updateSoundToggle();
        this.soundToggle?.addEventListener('click', this.onSoundToggle);
        this.video.addEventListener('volumechange', this.onVolumeChange);
        this.updateSoundToggle();
        this.toggle.addEventListener('click', this.onToggle);
        this.video.addEventListener('ended', this.onEnded);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.reducedMotion.addEventListener?.('change', this.onMotionChange);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(([entry]) => {
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
            this.syncPlayback();
          }, { threshold: [0, 0.35] });
          this.observer.observe(this);
        } else {
          this.inView = true;
        }

        this.syncPlayback();
      }

      disconnectedCallback() {
        this.video?.pause();
        this.observer?.disconnect();
        this.soundToggle?.removeEventListener('click', this.onSoundToggle);
        this.video?.removeEventListener('volumechange', this.onVolumeChange);
        this.toggle?.removeEventListener('click', this.onToggle);
        this.video?.removeEventListener('ended', this.onEnded);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.reducedMotion?.removeEventListener?.('change', this.onMotionChange);
      }

      playVideo() {
        if (this.documentHidden || !this.inView) {
          this.updateToggle();
          return;
        }
        this.video.loop = !this.reducedMotion.matches;
        const playRequest = this.video.play();
        if (playRequest?.then) {
          playRequest
            .then(() => {
              const canRemainPlaying = !this.video.paused
                && !this.userPaused
                && !this.documentHidden
                && this.inView
                && (!this.reducedMotion.matches || this.manualReducedMotionPlay);
              if (canRemainPlaying) {
                this.classList.add('is-video-playing');
              } else {
                this.video.pause();
                this.classList.remove('is-video-playing');
              }
              this.updateToggle();
            })
            .catch(() => {
              this.classList.remove('is-video-playing');
              this.updateToggle();
            });
        }
      }

      syncPlayback() {
        const reducedMotionBlocked = this.reducedMotion.matches && !this.manualReducedMotionPlay;
        const shouldPlay = !this.userPaused && !reducedMotionBlocked && !this.documentHidden && this.inView;
        this.video.loop = !this.reducedMotion.matches;

        if (shouldPlay) {
          this.playVideo();
        } else {
          this.video.pause();
          if (this.documentHidden || !this.inView) {
            this.manualReducedMotionPlay = false;
          }
          if (this.documentHidden || !this.inView || reducedMotionBlocked) {
            this.video.currentTime = 0;
            this.classList.remove('is-video-playing');
          }
          this.updateToggle();
        }
      }

      updateSoundToggle() {
        if (!this.soundToggle || !this.soundLabel) return;
        const muted = this.video.muted;
        this.soundToggle.setAttribute('aria-label', muted ? '動画のミュートを解除' : '動画をミュート');
        this.soundToggle.setAttribute('aria-pressed', String(muted));
        this.soundLabel.textContent = muted ? '音声 OFF' : '音声 ON';
      }

      updateToggle() {
        const playing = !this.video.paused;
        this.toggle.setAttribute('aria-label', playing ? '動画を一時停止' : '動画を再生');
        this.toggle.setAttribute('aria-pressed', String(playing));
        this.pauseIcon.hidden = !playing;
        this.playIcon.hidden = playing;
      }
    }

    customElements.define('titiroba-kitchen-media', TitirobaKitchenMedia);
  }
})();
